import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

/**
 * Per-session cache: avoids hitting has_role on every navigation between
 * /admin/* pages. We cache the in-flight Promise (not the resolved boolean)
 * so concurrent first-time consumers — e.g. UserMenu + RequireAdmin both
 * mounting on a cold-start direct-to-/admin load — share a single RPC
 * instead of racing two. Cleared on SIGNED_OUT.
 */
const cache = new Map<string, Promise<boolean>>();

let signOutListenerInstalled = false;
function installSignOutListener() {
  if (signOutListenerInstalled) return;
  signOutListenerInstalled = true;
  supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_OUT") cache.clear();
  });
}

function getAdminCheck(userId: string): Promise<boolean> {
  let p = cache.get(userId);
  if (!p) {
    p = Promise.resolve(supabase
      .rpc("has_role", { _user_id: userId, _role: "admin" })
      .then(({ data, error }) => {
        if (error) {
          // Don't poison the cache on transient errors — let next caller retry.
          cache.delete(userId);
          return false;
        }
        return data === true;
      }));
    cache.set(userId, p);
  }
  return p;
}

export function useIsAdmin() {
  const { session, isAuthReady } = useAuth();
  const userId = session?.user?.id ?? null;

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(isAuthReady && !userId);

  useEffect(() => {
    installSignOutListener();
  }, []);

  useEffect(() => {
    if (!isAuthReady) {
      setIsReady(false);
      return;
    }
    if (!userId) {
      setIsAdmin(false);
      setIsReady(true);
      return;
    }

    let cancelled = false;
    setIsReady(false);
    getAdminCheck(userId).then((ok) => {
      if (cancelled) return;
      setIsAdmin(ok);
      setIsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, isAuthReady]);

  return { isAdmin, isReady };
}
